import { Box, Button, Center, chakra, Divider, Heading, HStack, Icon, IconButton, Input, InputGroup, InputRightElement, Select, shouldForwardProp, Stack, Text, Tooltip, useDisclosure, useMediaQuery } from "@chakra-ui/react";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FaBolt, FaEdit, FaInfoCircle, FaPlus, FaTrashAlt, FaUndo } from "react-icons/fa";
import { BsChatText } from "react-icons/bs";
import { CustomServiceSelect } from "../../CreateBooking/WorkSelectorV2";
import { motion, isValidMotionProp, AnimatePresence } from "framer-motion";
import { OfferEnquiry } from "../../Marketplace/types";
import CheckAnimation from "../../Animations/CheckAnimation";
import { useCustomToast } from "../../../hooks/useCustomToast";
import { SETTINGS, useCompanySettings } from "../../../hooks/api/companySettings";
import { Endpoints } from "../../../hooks/api/endpoints";
import { axiosPost } from "../../../hooks/api/utils";
import { WorkArticleSelect } from "../../CreateBooking/WorkArticlesSelect";
import OfferMessageModal from "../OfferMessageModal";
import EditAddonArticlePercentageCostModal from "../EditAddonArticlePercentageCostModal";

const ChakraIcon = chakra(motion.div, {
    /**
     * Allow motion props and non-Chakra props to be forwarded.
     */
    shouldForwardProp: (prop) => isValidMotionProp(prop) || shouldForwardProp(prop),
});


// TODO: Move to backend as metasettings
export const units = [
    {
        key: 'meter',
        label: 'meter',
        abbreviation: 'm'
    },
    {
        key: 'unit',
        label: 'styck',
        abbreviation: 'st'
    },
    {
        key: 'liter',
        label: 'liter',
        abbreviation: 'L'
    }
]

export type ArticleDetails = {
    articleName: string;
    articlePrice: string;
    articleAmount: string;
    valueType: string;
    articleUnit: string;
    id: number;
}

type AddWorkDetailsFormProps = {
    rerender?: (val: any) => void;
    resetFields?: boolean;
    unFinishedWorkArticleError?: boolean;
    setUnfinishedWorkArticleError?: (val: boolean) => void;
    source?: 'offer' | 'additionalOffer' | 'booking'; // Slight different validation when rendering this when about to send new offer.
    articleFieldError?: Record<string, boolean>;
    setArticleFieldError?: (val: Record<string, boolean>) => void;
    articleRowRef?: MutableRefObject<any>;
    setArticleFieldsIsDirty?: (val: boolean) => void;
    selectedOfferEnquiry?: OfferEnquiry;
}

export function formatMoney(value: any): string {
    return new Intl.NumberFormat('sv-se', { style: 'currency', currency: 'SEK' }).format(value || 0);
}

export function getLaborCostSum(price: string, quantity: string) {
    if (!price) {
        return 0;
    }

    return parseFloat(price.replace(',', '.')) * parseFloat(quantity) || 0;
}

export function getCostSum(workArticles: ArticleDetails[], addonPercentage: string = '0'): { sum: number, addonSum: number } {
    let sum = 0;
    workArticles?.forEach(work => {
        if (work.articlePrice && work.articleAmount) {
            return sum += parseFloat(work.articlePrice.replace(',', '.')) * parseFloat(work.articleAmount)
        }
    })

    const addonDecimal = (parseFloat(addonPercentage) / 100) + 0;

    return {
        sum,
        addonSum: sum * addonDecimal
    };
}

export function getVatCost(sumTotal: number): number {
    return sumTotal - (sumTotal / 1.25);
}


// Dem kan också sätta en beskrivning om dem vill som är som en template typ, kanske här att vi kan använda Gemini åt dem.
export const VALUE_TYPE = {
    SPARE_PARTS: 'spareParts',
    WORK: 'work'
}

export const SELECTED_UNIT = {
    METER: 'meter',
    UNIT: 'unit'
}

const articleField = {
    articleName: 'articleName',
    articlePrice: 'articlePrice',
    articleAmount: 'articleAmount',
    articleUnit: 'articleUnit'
}

const AddWorkDetailsForm: React.FC<AddWorkDetailsFormProps> = ({ rerender, resetFields, unFinishedWorkArticleError, setUnfinishedWorkArticleError, source, articleFieldError, setArticleFieldError, articleRowRef, setArticleFieldsIsDirty, selectedOfferEnquiry }) => {
    // Keep local state of the added work for displayability
    const [addedWork, setAddedWork] = useState<ArticleDetails[]>([]);
    const { mutateCompanySettings, companySettings } = useCompanySettings();

    const hourlyWorkRateSetting = companySettings?.find(val => val.name === SETTINGS.HOURLY_WORK_RATE)?.value
    const addonArticleCostSetting = companySettings?.find(val => val.name === SETTINGS.ADDON_ARTICLE_PERCENTAGE_COST)?.value

    const initialArticleFormState = {
        articleName: '',
        articlePrice: '',
        articleAmount: '',
        valueType: VALUE_TYPE.SPARE_PARTS,
        articleUnit: SELECTED_UNIT.UNIT
    };

    const defaultHourlyRate = '1200';

    // TODO: Gär detta till en inställning som inlämningsdatum
    const initialLaborFormState = {
        laborCost: hourlyWorkRateSetting || defaultHourlyRate,
        laborHours: '1',
        articleAddonPercentageCost: '20',
        isDirtyLaborCost: false
    };

    const [articleFormValues, setArticleFormValues] = useState(initialArticleFormState);
    const [laborFormValues, setLaborFormValues] = useState(initialLaborFormState)
    const [unitValue, setUnitValue] = useState<string>(SELECTED_UNIT.UNIT)
    const [addMaterial, setAddMaterial] = useState<Boolean>(false)
    const [controlledReset, setControlledReset] = useState<number>(0)
    const [resetArticleSelect, setResetArticleSelect] = useState<boolean>(false);
    const { isOpen: isOpenAddonCostModal, onClose: onCloseAddonCostModal, onOpen: onOpenAddonCostModal } = useDisclosure();
    const { isOpen: isOpenMessageModal, onClose: onCloseMessageModal, onOpen: onOpenMessageModal } = useDisclosure();

    const [articleError, setArticleError] = useState<null | string>(null);
    const [workError, setWorkError] = useState<null | string>(null);
    const { successToast } = useCustomToast({ position: 'top-right' });
    const { sum, addonSum } = getCostSum(addedWork, addonArticleCostSetting);
    const sumTotal = sum + addonSum + getLaborCostSum(laborFormValues.laborCost, laborFormValues.laborHours);

    const { setValue, clearErrors, getValues } = useFormContext();
    const message = getValues('message');

    const errorRef = useRef(null);
    const hasDirtyArticleFields = articleFormValues.articleName?.length > 0 && articleFormValues.articleAmount?.length > 0 && articleFormValues.articlePrice?.length > 0;
    const isOffer = source === 'offer';
    const isSingleTroubleshootEnquiry = source === 'offer' && selectedOfferEnquiry?.reparations?.length === 1 && !selectedOfferEnquiry.service && selectedOfferEnquiry?.reparations[0]?.category === 'troubleshoot'
    const isServiceWorkOnly = source === 'offer' &&
        selectedOfferEnquiry?.service &&
        selectedOfferEnquiry?.reparations?.length === 0;

    const [isMobile] = useMediaQuery('(max-width: 492px)')
    const [isLoadingSave, setIsLoadingSave] = useState(false);
    const [isCompleteSave, setIsCompleteSave] = useState(false);

    useEffect(() => {
        const enquiryId = selectedOfferEnquiry?.offerEnquiryId;
        const savedValues = JSON.parse(sessionStorage.getItem(enquiryId))
        if (savedValues) {
            setValue('addedWork', savedValues.addedWork || [])
            setAddedWork(savedValues.addedWork || [])
            setValue('message', savedValues.message || null)
        }

        return () => {
            // Check if values exists, only then save them
            const dataToBeSavedForSpecificEnquiry = JSON.stringify({ addedWork: getValues('addedWork'), message: getValues('message') });
            sessionStorage.setItem(enquiryId, dataToBeSavedForSpecificEnquiry)
            setValue('addedWork', null)
            setValue('message', null)
            setAddedWork([])
        }
    }, [])

    useEffect(() => {
        let timeout = null;
        const laborValueNotChanged = hourlyWorkRateSetting === laborFormValues.laborCost;
        if (isCompleteSave || laborValueNotChanged) {
            timeout = setTimeout(() => {
                setLaborFormValues(prevValues => ({
                    ...prevValues,
                    isDirtyLaborCost: false
                }));
            }, 1500);
        }

        return () => clearTimeout(timeout)
    }, [setLaborFormValues, isCompleteSave, hourlyWorkRateSetting, laborFormValues.laborCost])

    // Set initial values here otherwise they will be undefined, later on we will fetch these from settings
    useEffect(() => {
        setValue('laborCost', initialLaborFormState.laborCost)
        setValue('laborHours', initialLaborFormState.laborHours)

        return () => {
            clearErrors()
        }
    }, [])

    useEffect(() => {
        if (resetFields) {
            setAddedWork([])
        }
    }, [resetFields])

    useEffect(() => {
        if (setArticleFieldsIsDirty && hasDirtyArticleFields) {
            setArticleFieldsIsDirty(hasDirtyArticleFields)
        }
    }, [setArticleFieldsIsDirty, hasDirtyArticleFields])

    const handleInputChange = (value: string | number, name: string, valueType?: typeof VALUE_TYPE.WORK | typeof VALUE_TYPE.SPARE_PARTS) => {
        setValue(name, value)
        setValue('currentValueType', valueType)

        // Update the state with the new value
        setArticleFormValues({
            ...articleFormValues,
            valueType,
            [name]: value,
        });
        if (setArticleFieldError) {
            setArticleFieldError({
                ...articleFieldError,
                [name]: false
            })
        }
    };

    // Function to handle changes to form field values
    const handleInputChangeLaborForm = (value: string | number, name: string) => {
        // Update the state with the new value
        setValue(name, value)
        if (rerender) {
            rerender({})
        }
        const isDirtyLaborCost = name === 'laborCost' && value !== initialLaborFormState.laborCost ? true : laborFormValues.isDirtyLaborCost
        if (isCompleteSave && isDirtyLaborCost) {
            setIsCompleteSave(false)
        }
        setLaborFormValues({
            ...laborFormValues,
            [name]: value,
            isDirtyLaborCost: isDirtyLaborCost
        });
    };

    const handleRemoveWorkRow = (id: number) => {
        const updatedWorkList = addedWork.filter(work => work.id !== id)

        setAddedWork(updatedWorkList);
        setValue('addedWork', updatedWorkList)
        setValue('errorRef', errorRef)
        setControlledReset(prev => prev - 1)

        if (rerender) {
            rerender({})
        }
    }

    function isValidArticleInputValues() {
        if (!articleFormValues.articleAmount ||
            !articleFormValues.articlePrice ||
            !articleFormValues.articleName ||
            Number(articleFormValues.articleAmount) <= 0 ||
            Number(articleFormValues.articlePrice) <= 0) {
            return false
        }
        return true
    }

    function isValidWorkInputValue() {
        if (articleFormValues.articleName?.length === 0) {
            return false
        }
        return true;
    }

    async function handleSaveHourlyRate() {
        setIsLoadingSave(true)
        try {
            const payload = {
                setting: SETTINGS.HOURLY_WORK_RATE,
                settingValue: laborFormValues.laborCost
            }
            await axiosPost(`${Endpoints.COMPANY_SETTINGS_V1}/update`, payload)
            await mutateCompanySettings();
            setIsLoadingSave(false)
            setIsCompleteSave(true)
            return successToast('Arbetskostnad i timmen ändrad')
        } catch (error) {
            setIsCompleteSave(false)
            setIsLoadingSave(false)
        }
    }

    function handleEditField(id: number, name: string, e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        const updatedWork = addedWork.map(work => {
            // Check for the correct work row by id
            if (work.id === id) {
                // Return a new object with the specific field updated
                return {
                    ...work,
                    [name]: value // Update the field dynamically
                };
            }
            return work; // Return the unchanged work object if no match
        });
        // Update the state with the modified array
        setAddedWork(updatedWork);
        setValue('addedWork', updatedWork)
    }

    function isValidArticleName(inputValue: string) {
        const regex = /^[a-z0-9\/\-\(\)\%\s]*$/i;

        if (regex.test(inputValue)) {
            return true
        }
        return false;
    }

    function isValidPrice(inputValue: string): boolean {
        const numbersOnlyRegex = /^[0-9\b.,]+$/;
        // remove leading zeros if presnet, 0900 -> 900
        inputValue = inputValue.replace(/^0+(?!$)/, '')
        // if value is not blank, then test the regex
        if (inputValue === '' || numbersOnlyRegex.test(inputValue)) {
            return true
        }
        return false
    }

    function isValidAmount(inputValue: string, articleUnit: string): boolean {
        if (articleUnit === 'unit') {
            const wholeNumbersOnly = /^[1-9]\d*$/;
            if (inputValue === '' || wholeNumbersOnly.test(inputValue)) {
                setArticleError(null)
                return true;
            }
            setArticleError('Styckmåttet måste vara ett heltal, välj måttet "Per liter" eller "Per meter" för att använda decimaltal')
            return false;
        } else {
            const decimalRegex = /^[0-9\b.,]+$/;
            // remove leading zeros if presnet, 0900 -> 900
            inputValue = inputValue.replace(/^0+(?!$)/, '')
            // if value is not blank, then test the regex
            if (inputValue === '' || decimalRegex.test(inputValue)) {
                return true;
            }
        }
    }

    function validatePrice(inputValue: string) {
        // if value is not blank, then test the regex
        if (isValidPrice(inputValue)) {
            handleInputChange(inputValue?.replace('.', ','), 'articlePrice')
        }
    }

    function validateAmount(inputValue: string, articleUnit: any) {
        if (isValidAmount(inputValue, articleUnit)) {
            handleInputChange(inputValue, 'articleAmount')
        }
    }

    return (
        <Box>
            <EditAddonArticlePercentageCostModal isOpen={isOpenAddonCostModal} onClose={onCloseAddonCostModal} action={addonArticleCostSetting ? 'edit' : 'add'} settingsValue={addonArticleCostSetting} />
            <OfferMessageModal isOpen={isOpenMessageModal} onClose={onCloseMessageModal} />
            <Stack pb={4}>
                <HStack>
                    <Heading size={'sm'}>Arbetets omfattning</Heading>
                </HStack>
            </Stack>
            {!isOffer && (
                <Box pb={0} pos='relative' data-testid='add-work-container'>
                    {workError && (
                        <Text pos={'absolute'} zIndex={9999} top={-4} fontSize={10} color={'red.500'}>{workError}</Text>
                    )}
                    <HStack align={'center'}>
                        <CustomServiceSelect source={source} resetKey={controlledReset} handleInputChange={(val) => handleInputChange(val, 'articleName', VALUE_TYPE.WORK)} />
                        <IconButton
                            colorScheme='blue'
                            aria-label='Add work'
                            data-testid='add-work-icon-button'
                            size={isMobile ? 'xs' : 'sm'}
                            icon={<FaPlus size={isMobile ? 10 : 16} />}
                            onClick={() => {
                                if (!isValidWorkInputValue()) {
                                    return setWorkError('Välj ett alternativ för arbete')
                                }

                                const id = addedWork.length + 1;
                                const listWithUpdatedValues = [...addedWork, { ...articleFormValues, valueType: VALUE_TYPE.WORK, id }]

                                setAddedWork(listWithUpdatedValues)
                                setValue('addedWork', listWithUpdatedValues)
                                setValue('articleName', null)
                                setValue('articlePrice', null)
                                setValue('articleAmount', null)

                                if (rerender) {
                                    rerender({})
                                }

                                // Reset form
                                clearErrors()
                                setArticleFormValues(initialArticleFormState)
                                setControlledReset(prev => prev + 1)
                                setArticleFieldError({})
                                setWorkError(null)
                                if (setUnfinishedWorkArticleError) {
                                    setUnfinishedWorkArticleError(false)
                                }
                            }} />
                    </HStack>
                </Box>
            )}

            <Stack spacing={2} pt={2}>
                {!isServiceWorkOnly && (
                    <HStack>
                        <Icon as={FaInfoCircle} color={'teal.700'} fontSize={16} _hover={{ color: 'teal.800' }} />
                        {isSingleTroubleshootEnquiry ? (
                            <Text fontSize={12}>Vid felsökningsofferter kan du endast skicka arbetskostnaden.</Text>
                        ) : (
                            <Text fontSize={12}>Om arbetet för reparation inte behöver reservdelar så kan du skippa att fylla i reservdelar.</Text>
                        )}
                    </HStack>
                )}
                {addedWork.map((work: ArticleDetails) => {
                    const valueType = work?.valueType
                    return (
                        <HStack key={work?.id} align={'center'} spacing={2} data-testid={`added-cost-stack-${work.articleName}`}>
                            <InputGroup
                                size='lg'
                                id={articleField.articleName}
                            >
                                {/* TODO: Add these in own component to avoid rerender of this component when using onMouseEnter and Leave */}
                                <Input
                                    name={articleField.articleName}
                                    id={articleField.articleName}
                                    type='text'
                                    readOnly={work.articleName && valueType === VALUE_TYPE.WORK}
                                    _placeholder={{ fontSize: 14 }}
                                    fontSize={[11, 16]}
                                    placeholder={valueType === 'spareParts' ? 'Namn på reservdel / material (styckpris)' : 'T.ex. Felsökning / Däckbyte / Däckskifte osv'}
                                    value={work.articleName}
                                    onChange={e => {
                                        if (isValidArticleName(e.target.value)) {
                                            handleEditField(work.id, articleField.articleName, e)
                                        }
                                    }}
                                />
                                {valueType === VALUE_TYPE.WORK && (
                                    <InputRightElement width={['8.5rem', '11.4rem']}>
                                        <Box bg={'gray.100'} p={1} pr={2} pl={2} borderRadius={16}>
                                            <Text fontSize={[8, 12]}>Ingår i arbetskostnaden</Text>
                                        </Box>
                                    </InputRightElement>
                                )}
                            </InputGroup>


                            {valueType === VALUE_TYPE.SPARE_PARTS && (
                                <>
                                    <InputGroup
                                        size='lg'
                                        id={articleField.articlePrice}
                                        w={['66%', '40%']}
                                        onFocus={(e) => e.target.addEventListener("wheel", (event) => event.preventDefault())}
                                    >
                                        <Input
                                            type='text'
                                            id={articleField.articlePrice}
                                            min={0}
                                            fontSize={[11, 16]}
                                            placeholder='Pris'
                                            value={work.articlePrice}
                                            onChange={e => {
                                                if (isValidPrice(e.target.value)) {
                                                    handleEditField(work.id, articleField.articlePrice, e)
                                                }
                                            }}
                                        />
                                        <InputRightElement width='2.5rem'>
                                            <Text fontSize={[11, 16]}>kr</Text>
                                        </InputRightElement>
                                    </InputGroup>

                                    <InputGroup size='lg' w={['52%', '40%']}
                                        id={articleField.articleAmount}
                                        onFocus={(e) => e.target.addEventListener("wheel", (event) => event.preventDefault())}
                                    >
                                        <Input
                                            type={`${work.articleUnit === 'unit' ? 'number' : 'text'}`}
                                            id={articleField.articleAmount}
                                            step={.01}
                                            min={1}
                                            fontSize={[11, 16]}
                                            placeholder='Ant.'
                                            value={work.articleAmount}
                                            onChange={e => {
                                                if (isValidAmount(e.target.value, work.articleUnit)) {
                                                    handleEditField(work.id, articleField.articleAmount, e)
                                                }
                                            }}
                                        />
                                        <InputRightElement width={['2.6rem', '3.0rem']} h={'3rem'}>
                                            <HStack spacing={0} p={0} m={0} align={'center'}>
                                                <Tooltip
                                                    hasArrow
                                                    bg='teal.600'
                                                    color='white'
                                                    placement='top'
                                                    borderRadius={12}
                                                    label={`Per ${units.find(unit => unit.key === work.articleUnit).label} - Tryck för att ändra`}
                                                >
                                                    <Select disabled={isSingleTroubleshootEnquiry} display={'flex'} iconSize={'16px'} left={isMobile ? 2 : 0} zIndex={2} variant='unstyled' cursor={'pointer'} fontSize={[11, 16]} value={work.articleUnit} onChange={(e) => {
                                                        handleEditField(work.id, articleField.articleUnit, e as any)
                                                    }} m={0} p={0}>
                                                        {units.map(unit => {
                                                            return (
                                                                <option key={unit.key} value={unit.key}>
                                                                    {unit.abbreviation}
                                                                </option>
                                                            )
                                                        })}
                                                    </Select>
                                                </Tooltip>
                                            </HStack>
                                        </InputRightElement>
                                    </InputGroup>
                                </>
                            )}
                            <Center onClick={() => handleRemoveWorkRow(work.id)} data-testid={`delete-added-cost-${work.articleName}`} cursor={'pointer'} p={[3, 4]} _hover={{ bg: 'red.200' }} bg={'red.100'} w={4} h={4} borderRadius='full'>
                                <Icon as={FaTrashAlt} fontSize={[10, 14]} />
                            </Center>
                        </HStack>
                    )
                })}
            </Stack>

            {!addMaterial && !isOffer && (
                <HStack cursor={'pointer'} color='blue.600' _hover={{ color: 'blue.400' }} w='max-content' spacing={1} pt={4} pb={2} onClick={() => {
                    // No validation needed when offer
                    if (isOffer) {
                        return setAddMaterial(true)
                    }

                    // At least one work item needs to be added before materials can be added
                    const allowedToAddMaterial = addedWork.some(work => work.valueType === 'work');
                    const isFieldDirty = articleFormValues.valueType === 'work' && articleFormValues.articleName?.length > 0

                    if (!allowedToAddMaterial && !isFieldDirty) {
                        return setWorkError('Välj ett arbete innan du lägger till reservdelar')
                    }

                    if (!allowedToAddMaterial && isFieldDirty) {
                        return setWorkError('Tryck på plus-knappen till höger för att lägga till arbetet')
                    }
                    setAddMaterial(true)
                }}>
                    <Heading fontSize={14} color='blue.600' _hover={{ color: 'blue.400' }} data-testid='add-spare-parts-or-material'>Lägg till reservdelar/material</Heading>
                    <Icon as={FaPlus} fontSize={14} />
                </HStack>
            )}

            {(addMaterial || isOffer) && (
                <Box h={!isServiceWorkOnly ? 16 : 16}>
                    <HStack align={'center'} spacing={[2, 2]} pt={2}>
                        <WorkArticleSelect articleFieldError={articleFieldError.workArticleSelect && !unFinishedWorkArticleError} setArticleFieldError={setArticleFieldError} disabled={isSingleTroubleshootEnquiry} resetArticleSelect={resetArticleSelect} setResetArticleSelect={setResetArticleSelect} placeholder={unitValue === SELECTED_UNIT.METER ? 'Tex Plåt / Bromsslang osv (Meterpris)' : unitValue === SELECTED_UNIT.UNIT ? 'Reservdel / material (Styckpris)' : 'Tex Motorolja (Literpris)'} resetKey={controlledReset} handleInputChange={val => handleInputChange(val, 'articleName')} />
                        <>
                            <InputGroup size='lg' w={['66%', '40%']}>
                                <Input
                                    type='text'
                                    inputMode="numeric"
                                    min={0}
                                    id={`${articleField.articlePrice}Write`}
                                    fontSize={[11, 16]}
                                    borderColor={articleFieldError?.articlePriceWrite ? 'red.500' : 'gray.300'}
                                    _focus={{ borderColor: articleFieldError?.articlePriceWrite ? 'red.500' : 'gray.300' }}
                                    onBlur={() => {
                                        if (articleFormValues?.articlePrice?.length > 0) {
                                            setArticleFieldError({})
                                        }
                                    }}
                                    placeholder='Pris'
                                    disabled={isSingleTroubleshootEnquiry}
                                    value={articleFormValues.articlePrice}
                                    data-testid="article-price-field"
                                    onChange={e => validatePrice(e.target.value)}
                                />
                                <InputRightElement width='2.0rem' p={0}>
                                    <Text fontSize={[11, 16]}>kr</Text>
                                </InputRightElement>
                                {articleFieldError?.articlePriceWrite && (
                                    <Text pos={'absolute'} bottom={-4} left={0} fontSize={10} color={'red.500'}>Fyll i priset</Text>
                                )}
                            </InputGroup>


                            <InputGroup size='lg' w={['52%', '40%']}>
                                <Input
                                    type='number'
                                    fontSize={[11, 16]}
                                    min={0}
                                    id={`${articleField.articleAmount}Write`}
                                    borderColor={articleFieldError?.articleAmountWrite ? 'red.500' : 'gray.300'}
                                    _focus={{ borderColor: articleFieldError?.articleAmountWrite ? 'red.500' : 'gray.300' }}
                                    onBlur={() => {
                                        if (articleFormValues?.articleAmount?.length > 0) {
                                            setArticleFieldError({})
                                        }
                                    }}
                                    disabled={isSingleTroubleshootEnquiry}
                                    value={articleFormValues.articleAmount}
                                    data-testid="article-unit-field"
                                    onChange={e => validateAmount(e.target.value, articleFormValues.articleUnit)}
                                />

                                <InputRightElement width={['2.6rem', '3.0rem']} h={'3rem'}>
                                    <HStack spacing={0} p={0} m={0} align={'center'}>
                                        <Select disabled={isSingleTroubleshootEnquiry} display={'flex'} iconSize={'16px'} left={isMobile ? 2 : 0} zIndex={2} variant='unstyled' cursor={'pointer'} fontSize={[11, 16]} value={articleFormValues.articleUnit} onChange={(e) => {
                                            const value = e.target.value;
                                            handleInputChange(value, 'articleUnit')
                                            setUnitValue(value)
                                        }} m={0} p={0}>
                                            {units.map(unit => {
                                                const isSelected = unit.key === articleFormValues.articleUnit;
                                                return (
                                                    <option key={unit.key} value={unit.key}>
                                                        {isSelected ? unit.abbreviation : `Per ${unit.label}`}
                                                    </option>
                                                )
                                            })}
                                        </Select>
                                    </HStack>
                                </InputRightElement>
                                {articleFieldError?.articleAmountWrite && (
                                    <Text pos={'absolute'} bottom={-4} left={0} fontSize={10} color={'red.500'}>Fyll i antalet</Text>
                                )}
                            </InputGroup>
                        </>

                        <Box pos={'relative'}>
                            {isValidArticleInputValues() && articleRowRef?.current && (
                                <ChakraIcon
                                    pos={'absolute'}
                                    right={-1}
                                    top={-2}
                                    zIndex={999}
                                    animate={{
                                        scale: [1, 1.2, 1, 1.2, 1],
                                    }}
                                    transition={{
                                        duration: 3,
                                        // @ts-ignore
                                        repeat: Infinity,
                                        repeatType: "loop",
                                    }}
                                >
                                    <Center bg={'teal.100'} borderRadius={'full'} p={1}>
                                        <Icon as={FaBolt} color={'teal.700'} fontSize={8} _hover={{ color: 'teal.800' }} />
                                    </Center>
                                </ChakraIcon>
                            )}
                            <IconButton
                                ref={hasDirtyArticleFields ? articleRowRef : null}
                                isDisabled={isSingleTroubleshootEnquiry}
                                colorScheme='blue'
                                aria-label='Add article'
                                data-testid='add-article-icon-button'
                                size={isMobile ? 'xs' : 'sm'}
                                icon={<FaPlus size={isMobile ? 10 : 16} />}
                                onClick={() => {
                                    if (!isValidArticleInputValues()) {
                                        return setArticleError('Fyll i fälten för att lägga till bilreservdelar i offerten')
                                    }

                                    const id = addedWork.length + 1;
                                    const listWithUpdatedValues = [...addedWork, { ...articleFormValues, valueType: VALUE_TYPE.SPARE_PARTS, id }]

                                    setAddedWork(listWithUpdatedValues)
                                    setValue('addedWork', listWithUpdatedValues)
                                    setValue('articleName', null)
                                    setValue('articlePrice', null)
                                    setValue('articleAmount', null)

                                    if (articleRowRef?.current) {
                                        articleRowRef.current.blur();  // Remove focus from the button
                                    }

                                    if (rerender) {
                                        rerender({})
                                    }

                                    // Reset form
                                    clearErrors();
                                    if (setArticleFieldsIsDirty) {
                                        setArticleFieldsIsDirty(false)
                                    }

                                    setArticleFormValues(initialArticleFormState)
                                    setResetArticleSelect(true);
                                    setUnitValue(SELECTED_UNIT.UNIT)
                                    setArticleError(null)
                                    if (setUnfinishedWorkArticleError) {
                                        setUnfinishedWorkArticleError(false)
                                    }
                                }} />
                        </Box>
                    </HStack>
                    {articleError && (
                        <Text fontSize={10} color={'red.500'}>{articleError}</Text>
                    )}

                    {(unFinishedWorkArticleError) && (
                        <Text fontSize={10} color={'red.500'}>{'Lägg till artikeln som du påbörjat med plus-knappen till höger'}</Text>
                    )}
                </Box>
            )}
            {source === 'offer' && (
                <Stack>
                    {message?.length > 0 ? (
                        <HStack align={'start'} pt={4}>
                            <Text p={1.5} px={3} bg={'gray.100'} borderRadius={12} fontSize={14} whiteSpace={'pre-wrap'}>{message}</Text>
                            <IconButton
                                colorScheme='gray'
                                aria-label='Edit message'
                                data-testid='edit-message-icon-button'
                                size={isMobile ? 'xs' : 'sm'}
                                icon={<FaEdit size={isMobile ? 10 : 16} />}
                                onClick={onOpenMessageModal} />
                        </HStack>
                    ) : (
                        <HStack pt={3}>
                            <HStack _hover={{ color: 'blue.400' }} cursor={'pointer'} onClick={onOpenMessageModal}>
                                <Heading fontSize={14} color='blue.600' data-testid='add-spare-parts-or-material'>Lägg till meddelande</Heading>
                                <Icon as={BsChatText} fontSize={16} color='blue.600' />
                            </HStack>
                        </HStack>
                    )}
                </Stack>
            )}

            <Box pos={'relative'}>
                <HStack pt={8}>
                    <Box w={'70%'}>
                        <Text fontSize={12}>Arbetskostnad i timmen</Text>
                        <Input
                            type='text'
                            size={'lg'}
                            inputMode="numeric"
                            data-testid='work-cost-per-hour-input'
                            value={laborFormValues.laborCost || hourlyWorkRateSetting}
                            onChange={e => {
                                const numbersOnlyRegex = /^[0-9\b]+$/;
                                let inputValue = e.target.value;

                                // if value is not blank, then test the regex
                                if (inputValue === '') {
                                    handleInputChangeLaborForm('0', 'laborCost')
                                } else if (numbersOnlyRegex.test(inputValue)) {
                                    // remove leading zeros if presnet, 0900 -> 900
                                    inputValue = inputValue.replace(/^0+(?!$)/, '')

                                    handleInputChangeLaborForm(inputValue, 'laborCost')
                                }
                            }}
                        />
                    </Box>
                    <Box w={'30%'}>
                        <Text fontSize={12}>Antal timmar</Text>
                        <Input
                            type='number'
                            size={'lg'}
                            onFocus={(e) => e.target.addEventListener("wheel", (event) => event.preventDefault())}
                            onBlur={(e) => e.target.removeEventListener("wheel", (event) => event.preventDefault())}
                            inputMode="numeric"
                            step={.01}
                            min={0}
                            data-testid='number-of-hours-input'
                            value={laborFormValues.laborHours}
                            onChange={e => {
                                let inputValue = e.target.value;
                                // remove leading zeros if presnet, 0900 -> 900
                                inputValue = inputValue.replace(/^0+(?!$)/, '')

                                handleInputChangeLaborForm(inputValue, 'laborHours')
                            }}
                        />
                    </Box>
                </HStack>
                <AnimatePresence>
                    {laborFormValues.isDirtyLaborCost && (
                        <motion.div
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -20, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Stack bg={'gray.100'}>
                                <Stack p={2}>
                                    <HStack spacing={4}>
                                        <Button isLoading={isLoadingSave} onClick={() => handleSaveHourlyRate()} rightIcon={<Icon as={FaBolt} size={4} />} size={'xs'} variant='secondary' borderRadius={16}>Kom ihåg kostnaden</Button>
                                        <Icon _hover={{ color: 'gray.600' }} as={FaUndo} cursor={'pointer'} onClick={() => setLaborFormValues({ ...laborFormValues, laborCost: hourlyWorkRateSetting, isDirtyLaborCost: false })} />
                                    </HStack>
                                    <HStack spacing={4}>
                                        <HStack>
                                            <Text fontSize={14}>Nuvarande tim-pris:</Text>
                                            <Heading size={'xs'}>{hourlyWorkRateSetting || defaultHourlyRate}kr</Heading>
                                        </HStack>
                                        <HStack>
                                            <Text fontSize={14}>Nytt tim-pris:</Text>
                                            <Heading size={'xs'}>{laborFormValues.laborCost}kr</Heading>
                                        </HStack>
                                        <CheckAnimation isChecked={isCompleteSave} h={20} w={20} />
                                    </HStack>
                                </Stack>
                            </Stack>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>

            <Box pt={6}>
                <Stack spacing={2}>
                    <HStack justify={'space-between'} align='start'>
                        <Box>
                            <Text>Materialkostnad</Text>
                            {addonArticleCostSetting && addonArticleCostSetting?.length > 0 ? (
                                <HStack align={'center'} _hover={{ color: 'blue.400' }} cursor={'pointer'} onClick={onOpenAddonCostModal}>
                                    <Text fontSize={14}>{`Varav påslag (${addonArticleCostSetting || 0}%)`}</Text>
                                    <Icon as={FaEdit} fontSize={14} color={'blue.600'} cursor={'pointer'} />
                                </HStack>
                            ) : (
                                <Text fontSize={14}>{`Varav påslag (${addonArticleCostSetting || 0}%)`}</Text>
                            )}
                        </Box>
                        <Stack align={'end'} spacing={0}>
                            <Text data-testid='material-cost-text'>{addonSum ? formatMoney(sum + addonSum) : formatMoney(sum)}</Text>
                            {addonArticleCostSetting && addonArticleCostSetting?.length > 0 ? (
                                <Text fontSize={14}>{formatMoney(addonSum)}</Text>
                            ) : (
                                <HStack _hover={{ color: 'blue.400' }} cursor={'pointer'} onClick={onOpenAddonCostModal}>
                                    <Heading fontSize={12} color='blue.600' data-testid='add-spare-parts-or-material'>Lägg till påslag</Heading>
                                    <Icon as={FaPlus} fontSize={12} color='blue.600' />
                                </HStack>
                            )}
                        </Stack>
                    </HStack>
                    <HStack justify={'space-between'} align='start'>
                        <Text fontSize={16}>Arbetskostnad</Text>
                        <Stack align={'flex-end'} spacing={0}>
                            <Text fontSize={16} data-testid='labour-cost-text'>{formatMoney(getLaborCostSum(laborFormValues.laborCost, laborFormValues.laborHours))}</Text>
                            <Text fontSize={14}>{laborFormValues.laborHours} timmar</Text>
                        </Stack>
                    </HStack>
                    <Divider pt={2} />
                    <Stack spacing={1}>
                        <HStack pt={2} justify={'space-between'} align='start'>
                            <Heading size={'sm'}>Totalt <Text fontSize={14} fontWeight={'normal'} as='span'>inkl. moms</Text></Heading>
                            <Heading size={'sm'} data-testid='total-cost-text'>{formatMoney(sumTotal)}</Heading>
                        </HStack>
                        <HStack justify={'space-between'} align='start'>
                            <Text fontSize={14}>{'Varav moms (25%)'}</Text>
                            <Text fontSize={14} data-testid='vat-cost-text'>{formatMoney(getVatCost(sumTotal))}</Text>
                        </HStack>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    )
}

export default AddWorkDetailsForm